import {TruffleFound} from '../model/truffelFound.model';

export interface YearlyYield {
  year: number;
  weight: number;
}

const toYear = (value?: string): number | undefined => {
  if (!value) {
    return undefined;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.getFullYear();
};

export const inferStartYear = (
  createdAt?: string,
  fallback?: number,
  truffleFounds: TruffleFound[] = []
): number => {
  const candidates: number[] = [];
  const createdYear = toYear(createdAt);
  if (createdYear !== undefined) {
    candidates.push(createdYear);
  }
  truffleFounds.forEach(found => {
    const year = toYear(found.foundAt);
    if (year !== undefined) {
      candidates.push(year);
    }
  });
  if (fallback !== undefined) {
    candidates.push(fallback);
  }
  if (candidates.length === 0) {
    return new Date().getFullYear();
  }
  return Math.min(...candidates);
};

export const calculateYearlyYield = (
  truffleFounds: TruffleFound[] = [],
  startYear?: number
): YearlyYield[] => {
  const totals = new Map<number, number>();
  truffleFounds.forEach(found => {
    const year = toYear(found.foundAt);
    if (year === undefined) {
      return;
    }
    const weight = Number(found.weight ?? 0);
    if (!Number.isFinite(weight)) {
      return;
    }
    totals.set(year, (totals.get(year) ?? 0) + weight);
  });

  let effectiveStart = startYear;
  if (effectiveStart === undefined) {
    const years = Array.from(totals.keys());
    effectiveStart = years.length > 0 ? Math.min(...years) : new Date().getFullYear();
  }

  const latestRecordedYear = totals.size > 0 ? Math.max(...totals.keys()) : effectiveStart;
  const endYear = Math.max(latestRecordedYear, new Date().getFullYear());

  const yields: YearlyYield[] = [];
  for (let year = effectiveStart; year <= endYear; year++) {
    yields.push({
      year,
      weight: Math.round((totals.get(year) ?? 0) * 100) / 100
    });
  }
  return yields;
};
